import { Outlet, type RouteObject } from 'react-router';
import { appConfig } from './config';
import { PublicLayout } from './layouts/PublicLayout';
import { ShellLayout } from './layouts/ShellLayout';
import { HomeRedirect, NotFoundPage, RouteErrorPage } from './pages/SystemPages';
import { SectionPage } from './pages/SectionPage';
import { sections as registeredSections, type SectionDefinition } from './registry';
import { RequireAuth, RequireClinic, RequireStaff } from './session/guards';
import { MfaChallengePage } from './session/MfaChallengePage';
import { AUTH_PATHS } from './session/paths';
import { HelpDrawer } from './shell/HelpDrawer';
import { HelpProvider } from './shell/HelpContext';

/** Корень: «Помощь» доступна на всех экранах, включая публичные (ПОРТ-61). */
function RootLayout() {
  return (
    <HelpProvider>
      <Outlet />
      <HelpDrawer />
    </HelpProvider>
  );
}

function sectionRoute(section: SectionDefinition): RouteObject {
  return { path: section.path, handle: { sectionId: section.id }, element: <SectionPage key={section.id} section={section} /> };
}

/** Полноэкранный раздел со своим макетом; витрина /_kit в разработке открывается без входа. */
function FullscreenSection({ section }: { section: SectionDefinition }) {
  if (section.devPublic && appConfig.kitPublic) return <SectionPage key={section.id} section={section} publicAccess />;
  return (
    <RequireAuth>
      <SectionPage key={section.id} section={section} />
    </RequireAuth>
  );
}

/** Маршруты приложения строятся из реестра разделов (src/app/registry.ts). */
export function buildRoutes(sections: ReadonlyArray<SectionDefinition> = registeredSections): RouteObject[] {
  const inShell = (area: 'clinic' | 'backoffice' | 'public') => sections.filter((section) => section.area === area && section.layout !== 'fullscreen');
  const fullscreen = sections.filter((section) => section.layout === 'fullscreen');
  return [
    {
      element: <RootLayout />,
      errorElement: <RouteErrorPage />,
      children: [
        {
          element: <PublicLayout />,
          children: [{ path: AUTH_PATHS.mfa, element: <MfaChallengePage /> }, ...inShell('public').map(sectionRoute)],
        },
        {
          path: '/',
          element: (
            <RequireAuth>
              <RequireClinic>
                <ShellLayout area="clinic" />
              </RequireClinic>
            </RequireAuth>
          ),
          children: [{ index: true, element: <HomeRedirect area="clinic" sections={sections} /> }, ...inShell('clinic').map(sectionRoute)],
        },
        {
          path: '/backoffice',
          element: (
            <RequireAuth>
              <RequireStaff>
                <ShellLayout area="backoffice" />
              </RequireStaff>
            </RequireAuth>
          ),
          children: [{ index: true, element: <HomeRedirect area="backoffice" sections={sections} /> }, ...inShell('backoffice').map(sectionRoute)],
        },
        ...fullscreen.map<RouteObject>((section) => ({ path: section.path, handle: { sectionId: section.id }, element: <FullscreenSection section={section} /> })),
        { path: '*', element: <NotFoundPage /> },
      ],
    },
  ];
}
